import Fuse, { IFuseOptions } from 'fuse.js';
import React from 'react';

type GetSearchIndexProps<T> = {
  items: T[];
  keys: IFuseOptions<T>['keys'];
  options?: Omit<IFuseOptions<T>, 'keys'>;
};

type SearchItemsProps<T> = GetSearchIndexProps<T> & {
  query: string;
};

const DEFAULT_FUSE_CONFIG = {
  distance: 5,
  threshold: 0.2,
};

export function getSearchIndex<T>({ items, keys, options }: GetSearchIndexProps<T>) {
  return new Fuse(items, { keys, ...DEFAULT_FUSE_CONFIG, ...options });
}

export function searchItems<T>({ items, keys, options, query }: SearchItemsProps<T>) {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) {
    return items;
  }

  return getSearchIndex({ items, keys, options })
    .search(trimmedQuery)
    .map((hit) => hit.item);
}

export function useLocalSearch<T>({ items, keys, options, query }: SearchItemsProps<T>) {
  const deferredQuery = React.useDeferredValue(query);
  const results = React.useMemo(
    () => searchItems({ items, keys, options, query: deferredQuery }),
    [items, keys, options, deferredQuery]
  );

  return { loading: deferredQuery !== query, results };
}
